<template>
  <div
    class="modal"
    :class="{ 'is-active': isActive }"
  >
    <div
      class="modal-background"
      @click="handleCancel"
    />
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          Add Authorized Representative
        </p>
        <button
          class="delete"
          aria-label="close"
          @click="handleCancel"
        />
      </header>
      <section class="modal-card-body">
        <!-- General Error Message -->
        <div
          v-if="errors.general"
          class="notification is-danger mb-3"
        >
          <button
            class="delete"
            @click="errors.general = null"
          />
          <i class="fas fa-exclamation-circle mr-2" />
          {{ errors.general }}
        </div>

        <callout type="warning">Can't find your authorized representative? They need to be added to the City system before you can assign them to a property. Submit an application through the regular flow to add them.</callout>

        <!-- Search Step -->
        <div v-if="currentStep === 'search'">
          <p class="mb-4">
            Search for existing representative by email
          </p>

          <div class="field">
            <label class="label">Email Address *</label>
            <div class="control">
              <input
                v-model="searchData.email"
                class="input"
                type="email"
                placeholder="email@example.com"
                :class="{ 'is-danger': errors.email }"
                @keyup.enter="handleSearch"
              >
            </div>
            <p
              v-if="errors.email"
              class="help is-danger"
            >
              {{ errors.email }}
            </p>
          </div>
        </div>

        <!-- Results Step -->
        <div v-else-if="currentStep === 'select'">
          <!-- No Results -->
          <div v-if="searchResults.length === 0">
            <div class="notification is-warning">
              <i class="fas fa-exclamation-triangle mr-2" />
              No representatives found matching your search.
            </div>
            <p class="mt-3">
              <a
                class="has-text-ben-franklin-blue"
                @click="backToSearch"
              >
                <i class="fas fa-arrow-left mr-2" />Back to Search
              </a>
            </p>
          </div>

          <!-- Single Result -->
          <div v-else-if="searchResults.length === 1">
            <p class="mb-3">
              <strong>Representative found:</strong>
            </p>
            <div class="box has-background-ghost-gray">
              <p class="is-size-5 has-text-weight-semibold">
                {{ searchResults[0].full_name || searchResults[0].name || 'N/A' }}
              </p>
              <div class="columns is-mobile mt-1">
                <div class="column is-half">
                  <span class="field-label has-text-weight-semibold is-size-7 has-text-medium-gray">EMAIL</span><br>
                  <span class="has-text-dark-gray">{{ searchResults[0].email || 'N/A' }}</span>
                </div>
                <div class="column is-half">
                  <span class="field-label has-text-weight-semibold is-size-7 has-text-medium-gray">PHONE</span><br>
                  <span class="has-text-dark-gray">{{ searchResults[0].phone_number || 'N/A' }}</span>
                </div>
              </div>
              <div class="columns is-mobile">
                <div class="column is-half">
                  <span class="field-label has-text-weight-semibold is-size-7 has-text-medium-gray">ORGANIZATION</span><br>
                  <span class="has-text-dark-gray">{{ searchResults[0].organization_name || 'N/A' }}</span>
                </div>
                <div class="column is-half">
                  <span class="field-label has-text-weight-semibold is-size-7 has-text-medium-gray">ROLE</span><br>
                  <span class="has-text-dark-gray">{{ formatRole(searchResults[0].role) }}</span>
                </div>
              </div>
            </div>

            <!-- Already Added Warning -->
            <div
              v-if="isAlreadyAdded"
              class="notification is-warning mt-3"
            >
              <i class="fas fa-exclamation-triangle mr-2" />
              This representative has already been added.
            </div>

            <p class="mt-3">
              <a
                class="has-text-ben-franklin-blue"
                @click="backToSearch"
              >
                <i class="fas fa-arrow-left mr-2" />Back to Search
              </a>
            </p>
          </div>

          <!-- Multiple Results -->
          <div v-else>
            <p class="mb-3">
              <strong>{{ searchResults.length }} Representatives found:</strong>
            </p>
            <p class="mb-3">
              Select one:
            </p>

            <div class="search-results">
              <div
                v-for="rep in searchResults"
                :key="rep.rid"
                class="box mb-3"
                :class="{ 'selected-rep': selectedRep && (selectedRep.rid) === (rep.rid) }"
                @click="selectRep(rep)"
              >
                <div class="field">
                  <div class="control">
                    <label class="radio">
                      <input
                        v-model="selectedRepId"
                        type="radio"
                        :value="rep.rid"
                        @change="selectRep(rep)"
                      >
                      <span class="ml-2">
                        <strong>{{ rep.full_name || 'NA' }}</strong>
                      </span>
                    </label>
                  </div>
                  <div class="ml-4 mt-1">
                    <div class="columns is-mobile">
                      <div class="column is-half">
                        <span class="field-label has-text-weight-semibold is-size-7 has-text-medium-gray">EMAIL</span><br>
                        <span class="has-text-dark-gray">{{ rep.email || 'N/A' }}</span>
                      </div>
                      <div class="column is-half">
                        <span class="field-label has-text-weight-semibold is-size-7 has-text-medium-gray">PHONE</span><br>
                        <span class="has-text-dark-gray">{{ rep.phone_number || 'N/A' }}</span>
                      </div>
                    </div>
                    <div class="columns is-mobile">
                      <div class="column is-half">
                        <span class="field-label has-text-weight-semibold is-size-7 has-text-medium-gray">ORGANIZATION</span><br>
                        <span class="has-text-dark-gray">{{ rep.organization_name || 'N/A' }}</span>
                      </div>
                      <div class="column is-half">
                        <span class="field-label has-text-weight-semibold is-size-7 has-text-medium-gray">ROLE</span><br>
                        <span class="has-text-dark-gray">{{ formatRole(rep.role) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Already Added Warning -->
            <div
              v-if="isAlreadyAdded"
              class="notification is-warning mt-3"
            >
              <i class="fas fa-exclamation-triangle mr-2" />
              This representative has already been added.
            </div>

            <p class="mt-3">
              <a
                class="has-text-ben-franklin-blue"
                @click="backToSearch"
              >
                <i class="fas fa-arrow-left mr-2" />Back to Search
              </a>
            </p>
          </div>
        </div>
      </section>

      <footer class="modal-card-foot">
        <div class="buttons">
          <button
            class="button is-primary"
            :class="{ 'is-loading': isSearching }"
            :disabled="actionButtonDisabled"
            @click="handleAction"
          >
            <span class="icon">
              <i :class="actionButtonIcon" />
            </span>
            <span>{{ actionButtonText }}</span>
          </button>

          <button
            class="button is-secondary"
            :disabled="isSearching"
            @click="handleCancel"
          >
            Cancel
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { handleARSearch, handleARAdd } from "@/utilities/dashboard/index.js";

export default {
  name: "AddRepresentativeModal",
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    existingRepIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentStep: 'search', // 'search' or 'select'
      searchData: {
        email: '',
      },
      searchResults: [],
      selectedRep: null,
      selectedRepId: null,
      isSearching: false,
      errors: {},
    };
  },
  computed: {
    actionButtonText() {
      if (this.currentStep === 'search') {
        return 'Search';
      }
      return 'Add Representative';
    },

    actionButtonIcon() {
      if (this.currentStep === 'search') {
        return 'fas fa-search';
      }
      return 'fas fa-user-plus';
    },

    actionButtonDisabled() {
      if (this.currentStep === 'search') {
        // Disable if search fields are empty/invalid
        const email = this.searchData.email.trim();
        const isValid = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        return !isValid;
      }
      // In select step, disable if no rep selected or if already added
      if (this.searchResults.length === 1) {
        // Single result is auto-selected
        return this.isAlreadyAdded;
      }
      return !this.selectedRep || this.isAlreadyAdded;
    },

    isAlreadyAdded() {
      if (!this.selectedRep) {
        return false;
      }
      const repId = this.selectedRep.rid;
      return this.existingRepIds.includes(repId);
    },
  },
  methods: {
    validateSearch() {
      this.errors = {};
      let isValid = true;

      // Email validation
      if (!this.searchData.email || this.searchData.email.trim() === '') {
        this.errors.email = 'Email address is required';
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.searchData.email)) {
        this.errors.email = 'Please enter a valid email address';
        isValid = false;
      }

      return isValid;
    },

    async handleSearch() {
      if (!this.validateSearch()) {
        return;
      }

      this.isSearching = true;

      try {
        // Make API call to search for user
        const searchEmail = this.searchData.email.trim().toLowerCase();
        const roles = ["PMR", "Mediation Representative"];

        const result = await handleARSearch(searchEmail, roles);
        const data = await result.json();
        if (result.ok && data.users && data.users.length > 0) {
          this.searchResults = data.users;
          this.currentStep = 'select';

          // Auto-select only if single result
          if (data.users.length === 1) {
            this.selectedRep = data.users[0];
            this.selectedRepId = data.users[0].rid;
          }
        } else {
          console.log("RESULTS", data);
          // handle errors here. i.e user not found, email invalid, etc
          if (data.error) {
            // Display error code and message from backend
            this.errors.email = `[${data.error.code}] ${data.error.message}`;
          } else {
            this.errors.email = 'Unable to perform search. Please try again later.';
          }
        }
      } catch (error) { //should never reach here 
        console.error('Unexpected error during search:', error);
        this.errors.email = 'Unable to perform search. Please try again later.';
       
      } finally{
        this.isSearching = false;
      }

    },

    async handleAddRepresentative() {
      if (!this.selectedRep || this.isAlreadyAdded) {
        return;
      }

      this.isSearching = true;

      try {
        // Get organization_rid from current user in Vuex store
        const userInfo = this.$store.getters['auth/userInfo'];
        const organization_rid = userInfo?.qbUser?.related_organization;

        if (!organization_rid) {
          this.errors.general = 'Unable to determine your organization. Please try again.';
          this.isSearching = false;
          return;
        }

        // Call API to add authorized representative
        const response = await handleARAdd(organization_rid, this.selectedRep);
        const data = await response.json();

        if (response.ok && data.authorized_rep) {
          // Emit success event with the created AR data
          this.$emit('submit', data.authorized_rep);
          this.resetForm();
        } else {
          // Show error message
          if (data.error) {
            this.errors.general = `[${data.error.code}] ${data.error.message}`;
          } else {
            this.errors.general = 'Failed to add authorized representative';
          }
        }
      } catch (error) {
        console.error('Unexpected error adding representative:', error);
        this.errors.general = 'An unexpected error occurred. Please try again.';
      } finally {
        this.isSearching = false;
      }
    },

    handleAction() {
      if (this.currentStep === 'search') {
        this.handleSearch();
      } else {
        this.handleAddRepresentative();
      }
    },

    selectRep(rep) {
      this.selectedRep = rep;
      this.selectedRepId = rep.rid;
    },

    formatRole(role) {
      const roleMap = {
        'ar': 'PMR',
        'Mediation Representative': 'Mediation Representative'
      };
      return roleMap[role] || role || 'N/A';
    },

    backToSearch() {
      this.currentStep = 'search';
      this.searchResults = [];
      this.selectedRep = null;
      this.selectedRepId = null;
      this.errors = {};
    },

    handleCancel() {
      this.$emit('close');
      this.resetForm();
    },

    resetForm() {
      this.currentStep = 'search';
      this.searchData = {
        email: '',
      };
      this.searchResults = [];
      this.selectedRep = null;
      this.selectedRepId = null;
      this.errors = {};
    },
  },
};
</script>

<style scoped>
.modal-card {
  max-width: 600px;
}

.modal-card-foot {
  justify-content: flex-start;
}

.has-text-ben-franklin-blue {
  color: #2176d2;
  cursor: pointer;
  text-decoration: underline;
}

.has-text-ben-franklin-blue:hover {
  color: #0f4d90;
}

.has-text-dark-gray {
  color: #444444;
}

.has-text-medium-gray {
  color: #a1a1a1;
}

.has-background-ghost-gray {
  background-color: #f0f0f0;
}

.selected-rep {
  border: 2px solid #2176d2;
  background-color: #daedfe;
}

.search-results {
  max-height: 400px;
  overflow-y: auto;
}

.columns {
  margin-bottom: 0 !important;
}

.columns:not(:last-child) {
  margin-bottom: 0.5rem !important;
}

.box {
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #cfcfcf;
}

.box:hover {
  background-color: #f0f0f0;
  border-color: #a1a1a1;
}

.mb-3 {
  margin-bottom: 0.75rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mt-1 {
  margin-top: 0.25rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 0.75rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.ml-4 {
  margin-left: 1rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.is-size-7 {
  font-size: 0.75rem !important;
}

.has-text-weight-semibold {
  font-weight: 600 !important;
}

.field-label {
  text-decoration: underline;
}

input::placeholder {
  color: #999999 !important;
  opacity: 1 !important;
}
</style>